import type { Service } from "@oathera/contract";
import type { LiveStreamStore } from "../../state/live-stream.ts";
import type { StreamViewStore } from "../../state/stream-view.ts";
import {
  CLOSE_POLICY,
  CLOSE_SLOW_CLIENT,
  makeSnapshotRequestFrame,
  makeSubscribeFrame,
  parseServerFrame,
  serializeClientFrame,
  snapshotDisclosesGap,
  STREAM_TOPICS,
  type ServerFrame,
  type StreamTopic,
} from "./frames.ts";
import {
  backoffDelay,
  SSE_BACKOFF,
  WEBSOCKET_BACKOFF,
  type BackoffPolicy,
} from "./backoff.ts";
import {
  bufferedCount,
  createPauseBuffer,
  drain,
  enqueue,
  type PauseBuffer,
} from "./pause-buffer.ts";
import { applyFrame, EMPTY_VIEW, type StreamView } from "./view-state.ts";

/**
 * The live-stream client (task 11.3, design 8.1 / 12.3).
 *
 * It opens the hub stream SAME-ORIGIN through the BFF — a WebSocket at `/api/v1/stream`, falling
 * back to Server-Sent Events at `/api/v1/stream/sse` when sockets cannot be established — and keeps
 * the view fed through disconnects. It owns no React; it writes two stores: the connection store
 * (`LiveStreamStore`, for the indicator) and the view store (`StreamViewStore`, for the screens).
 * A thin React hook (`stream-view.tsx`) starts/stops it and feeds it the pause state.
 *
 * Protocol (design 8.1), exactly:
 * - On connect it sends a `subscribe` with the stored resume token if it has one. The hub replies
 *   either with DELTAS (a valid, in-range token: a gap-free resume, no snapshot) or with a SNAPSHOT
 *   whose `reason` is `initial` (no token), `resume_invalid` (token did not verify) or `resume_gap`
 *   (token ahead of head / below a pruned floor). On any snapshot the client applies a FRESH
 *   snapshot before new deltas, discloses a gap for every reason but `initial`, and continues from
 *   the snapshot's position.
 * - Every frame carries `pos` and `resume_token`; the client stores the latest token (opaque, never
 *   parsed) and ignores a `pos` it has already seen (dedupe is in the reducer).
 * - On a drop it reconnects with exponential backoff + jitter, resuming with the stored token. On a
 *   `bad_frame`/policy close it drops the token and resubscribes fresh. On `slow_client` it resumes.
 *   On a role/no-topics close it surfaces an error and does not hammer-retry.
 * - When PAUSED, deltas are parked in a bounded buffer and the buffered count is published; on
 *   resume without overflow they replay in order; on overflow the client requests a fresh snapshot
 *   and discloses the gap. Snapshots always apply even while paused (they are the recovery path).
 */

/** How the SPA addresses the hub through the BFF (same origin). */
const WS_PATH = "/api/v1/stream";
const SSE_PATH = "/api/v1/stream/sse";

/** Repeated handshake failures before giving up on WebSocket and falling back to SSE. */
const WS_FAILURES_BEFORE_SSE = 2;

export interface StreamClientOptions {
  /** The connection store the indicator reads. */
  liveStore: LiveStreamStore;
  /** The view store the screens read. */
  viewStore: StreamViewStore;
  /** The service filter to subscribe with, or null for all services. */
  service?: Service | null;
  /** The topics to subscribe to; defaults to all the hub carries. */
  topics?: readonly StreamTopic[];
  /** The pause-buffer bound; defaults to the module default. */
  pauseBufferLimit?: number;
  // Seams for tests: constructors and the clock. The browser globals are the defaults.
  now?: () => number;
  random?: () => number;
  makeWebSocket?: (url: string) => WebSocketLike;
  makeEventSource?: (url: string) => EventSourceLike;
}

/** The minimal WebSocket surface the client uses (so a test can supply a fake). */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((this: unknown, ev: unknown) => void) | null;
  onmessage: ((this: unknown, ev: { data: unknown }) => void) | null;
  onerror: ((this: unknown, ev: unknown) => void) | null;
  onclose: ((this: unknown, ev: { code: number; reason: string }) => void) | null;
}

/** The minimal EventSource surface the client uses. */
export interface EventSourceLike {
  close(): void;
  onopen: ((this: unknown, ev: unknown) => void) | null;
  onmessage: ((this: unknown, ev: { data: unknown }) => void) | null;
  onerror: ((this: unknown, ev: unknown) => void) | null;
}

type Transport = "websocket" | "sse";

export class StreamClient {
  private readonly liveStore: LiveStreamStore;
  private readonly viewStore: StreamViewStore;
  private readonly service: Service | null;
  private readonly topics: readonly StreamTopic[];
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly makeWebSocket: (url: string) => WebSocketLike;
  private readonly makeEventSource: (url: string) => EventSourceLike;

  /** The opaque resume token last seen (never parsed). Null before any frame, or after a reset. */
  private resumeToken: string | null = null;
  /** The current view (the reducer's state). The view store mirrors it. */
  private view: StreamView = EMPTY_VIEW;
  /** The pause buffer; only used while paused. */
  private buffer: PauseBuffer;
  private paused = false;

  /** Whether the operator intends the stream to run; set by `start`, cleared by `stop`. */
  private running = false;
  /** The chosen transport this attempt; starts on WebSocket and falls back to SSE. */
  private transport: Transport = "websocket";
  /** Consecutive WebSocket handshake failures (drives the SSE fallback). */
  private wsFailures = 0;
  /** The reconnect attempt counter (drives backoff); reset on a healthy open. */
  private attempt = 0;

  private ws: WebSocketLike | null = null;
  private es: EventSourceLike | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** True once a frame arrived on the current connection: the connection is healthy. */
  private sawFrameThisConnection = false;

  constructor(options: StreamClientOptions) {
    this.liveStore = options.liveStore;
    this.viewStore = options.viewStore;
    this.service = options.service ?? null;
    this.topics = options.topics ?? STREAM_TOPICS;
    this.now = options.now ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));
    this.random = options.random ?? Math.random;
    this.makeWebSocket =
      options.makeWebSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
    this.makeEventSource =
      options.makeEventSource ?? ((url) => new EventSource(url) as unknown as EventSourceLike);
    this.buffer = createPauseBuffer(options.pauseBufferLimit);
  }

  /** Open the stream and keep it open across disconnects. Idempotent. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.transport = "websocket";
    this.wsFailures = 0;
    this.attempt = 0;
    this.connect();
  }

  /** Close the stream and stop reconnecting. Idempotent. */
  stop(): void {
    this.running = false;
    this.clearReconnect();
    this.teardownConnection();
    this.liveStore.setConnection("disconnected", { detail: null });
  }

  /**
   * Set the pause state. Entering pause starts buffering deltas; leaving pause either replays the
   * buffered deltas in order (no overflow) or, if the buffer overflowed, requests a fresh snapshot
   * and discloses the gap.
   */
  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    this.viewStore.setPaused(paused);
    if (paused) {
      // Begin a clean buffer for this pause.
      this.buffer = createPauseBuffer(this.buffer.limit);
      this.viewStore.setBufferedCount(0);
      return;
    }
    this.onResume();
  }

  // --- connection lifecycle -------------------------------------------------------------------

  private connect(): void {
    if (!this.running) return;
    this.clearReconnect();
    this.sawFrameThisConnection = false;
    this.liveStore.setConnection("connecting", {
      transport: this.transport,
      detail: this.transport === "sse" ? "sockets blocked, using SSE" : null,
    });
    if (this.transport === "websocket") this.openWebSocket();
    else this.openEventSource();
  }

  private openWebSocket(): void {
    const url = this.streamUrl(WS_PATH);
    let ws: WebSocketLike;
    try {
      ws = this.makeWebSocket(url);
    } catch {
      // The runtime refused to even construct a socket (blocked): fall back to SSE immediately.
      this.wsFailures = WS_FAILURES_BEFORE_SSE;
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      // The socket is open; the hub expects the first client frame to be `subscribe`.
      this.sendSubscribe();
      this.liveStore.setConnection("connected", { transport: "websocket", detail: null });
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") this.onFrameText(ev.data);
    };
    ws.onerror = () => {
      // An error before any frame is a handshake failure; count it toward the SSE fallback.
      if (!this.sawFrameThisConnection) this.wsFailures += 1;
    };
    ws.onclose = (ev) => {
      this.ws = null;
      this.onConnectionClosed(ev.code);
    };
  }

  private openEventSource(): void {
    // EventSource repeats its first URL on reconnect and sends `Last-Event-ID` itself, which the hub
    // prefers over the query token (design 8.1 "SSE"). So the first open carries the resume token in
    // the query; the browser's own reconnect uses the header. The client treats an SSE `error` as a
    // drop and reconnects on its own backoff, which also covers the case where EventSource gives up.
    const url = this.streamUrl(SSE_PATH);
    let es: EventSourceLike;
    try {
      es = this.makeEventSource(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.es = es;
    es.onopen = () => {
      this.liveStore.setConnection("connected", {
        transport: "sse",
        detail: "sockets blocked, using SSE",
      });
    };
    es.onmessage = (ev) => {
      if (typeof ev.data === "string") this.onFrameText(ev.data);
    };
    es.onerror = () => {
      // SSE has no subscribe frame; topics/filters/token are in the URL. On error, reconnect.
      this.es = null;
      try {
        es.close();
      } catch {
        /* already closing */
      }
      this.onConnectionClosed(null);
    };
  }

  /** Build the stream URL with topics, the service filter and (for SSE's first open) the token. */
  private streamUrl(path: string): string {
    const params = new URLSearchParams();
    params.set("topics", this.topics.join(","));
    if (this.service !== null) params.set("service", this.service);
    // SSE has no client frames, so the first subscribe parameters ride the query. The browser's
    // own reconnect then uses `Last-Event-ID`, which the hub prefers. For WebSocket the subscribe is
    // a frame, but passing the same query is harmless and lets the BFF see the topics.
    if (path === SSE_PATH && this.resumeToken !== null) params.set("resume_token", this.resumeToken);
    return `${path}?${params.toString()}`;
  }

  private sendSubscribe(): void {
    const frame = makeSubscribeFrame({
      topics: this.topics,
      service: this.service,
      resumeToken: this.resumeToken,
    });
    this.sendClientFrame(frame);
  }

  private sendClientFrame(frame: Parameters<typeof serializeClientFrame>[0]): void {
    if (this.ws === null) return;
    try {
      this.ws.send(serializeClientFrame(frame));
    } catch {
      // A send failure means the socket is going away; the close handler will reconnect.
    }
  }

  // --- inbound frames -------------------------------------------------------------------------

  private onFrameText(text: string): void {
    const frame = parseServerFrame(text);
    if (frame === null) return; // not a readable server frame; drop it (the hub would not send one)
    this.sawFrameThisConnection = true;
    // A healthy connection resets the backoff and the WS-failure count.
    this.attempt = 0;
    this.wsFailures = 0;
    this.liveStore.noteActivity(this.now());
    // Store the latest opaque resume token so a reconnect resumes from here.
    this.resumeToken = frame.resume_token;
    this.liveStore.setResumeToken(frame.resume_token);
    this.ingest(frame);
  }

  /**
   * Fold one inbound frame into the view, honouring the pause state. A snapshot ALWAYS applies
   * (fresh snapshot before new deltas; it is also the recovery path after a gap). A delta applies
   * live, or is buffered while paused.
   */
  private ingest(frame: ServerFrame): void {
    if (frame.type === "snapshot") {
      // Fresh snapshot: replace the topic's view, then continue with deltas after it.
      this.view = applyFrame(this.view, frame);
      this.viewStore.setView(this.view);
      if (snapshotDisclosesGap(frame.reason)) {
        // initial = no gap; resume_invalid / resume_gap / client_requested = a gap was bridged.
        this.viewStore.setGapDisclosed(true);
      } else {
        this.viewStore.setGapDisclosed(false);
      }
      // A snapshot settles any pending overflow recovery: start a clean buffer if still paused.
      if (this.paused) {
        this.buffer = createPauseBuffer(this.buffer.limit);
        this.viewStore.setBufferedCount(0);
      }
      // If this snapshot was requested to recover an overflow and we are now live, the gap stays
      // disclosed only for a resume_* reason; a client_requested snapshot clears the buffered count.
      return;
    }

    // A delta.
    if (this.paused) {
      const next = enqueue(this.buffer, frame);
      this.buffer = next;
      this.viewStore.setBufferedCount(bufferedCount(next));
      if (next.overflowed) {
        // The buffer overflowed: an ordered replay is no longer possible. Disclose the gap now; the
        // fresh snapshot is requested on resume (we keep showing the frozen view until then).
        this.viewStore.setGapDisclosed(true);
      }
      return;
    }
    // Live: fold straight into the view (the reducer dedupes by pos).
    const nextView = applyFrame(this.view, frame);
    if (nextView !== this.view) {
      this.view = nextView;
      this.viewStore.setView(nextView);
    }
  }

  private onResume(): void {
    if (this.buffer.overflowed) {
      // Overflow: discard the incomplete range and ask the hub for a fresh snapshot of every topic.
      // The snapshot(s) replace the view and the gap stays disclosed until they settle it. We do NOT
      // replay (the range is incomplete).
      this.buffer = createPauseBuffer(this.buffer.limit);
      this.viewStore.setBufferedCount(0);
      this.requestFreshSnapshot();
      return;
    }
    // No overflow: replay the buffered deltas in order, then continue live.
    const { frames, next } = drain(this.buffer);
    this.buffer = next;
    this.viewStore.setBufferedCount(0);
    let view = this.view;
    for (const frame of frames) view = applyFrame(view, frame);
    if (view !== this.view) {
      this.view = view;
      this.viewStore.setView(view);
    }
  }

  /**
   * Ask the hub for a fresh snapshot after an overflow. On a WebSocket, send one `snapshot_request`
   * per subscribed topic (the hub answers each with a `client_requested` snapshot and continues). On
   * SSE there are no client frames, so reconnect fresh WITHOUT the resume token — a tokenless
   * subscribe gets an `initial` snapshot — which is the SSE equivalent of a fresh snapshot.
   */
  private requestFreshSnapshot(): void {
    if (this.transport === "websocket" && this.ws !== null) {
      for (const topic of this.topics) this.sendClientFrame(makeSnapshotRequestFrame(topic));
      return;
    }
    // SSE (or no live socket): drop the token and reconnect; a tokenless subscribe is a fresh start.
    this.resumeToken = null;
    this.liveStore.setResumeToken(null);
    this.teardownConnection();
    this.connect();
  }

  // --- disconnect / reconnect -----------------------------------------------------------------

  private onConnectionClosed(code: number | null): void {
    if (!this.running) return;

    if (code === CLOSE_POLICY) {
      // 1008: either a bad frame (the client should never cause one, but recover fresh) or the role
      // has no topics / is refused. We cannot tell the two apart from the close code alone, and the
      // contract has no error frame. A bad-frame recovery is a fresh resubscribe; a role refusal
      // must NOT hammer-retry. We treat it as a terminal error state after dropping the token: the
      // client surfaces `error` and backs off hard rather than looping tightly.
      this.resumeToken = null;
      this.liveStore.setResumeToken(null);
      this.liveStore.setConnection("error", {
        detail: "the stream was refused; retrying",
      });
      // Back off on the error path too, but do not give up entirely (a transient bad frame clears).
      this.scheduleReconnect();
      return;
    }

    // 1013 slow_client, a normal close, or an SSE error: reconnect and resume with the stored token.
    this.liveStore.setConnection("connecting", {
      transport: this.transport,
      detail: code === CLOSE_SLOW_CLIENT ? "reconnecting (slow client)" : null,
    });
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    this.clearReconnect();

    // Decide the transport for the NEXT attempt: fall back to SSE after repeated WS handshake
    // failures, and once on SSE, stay there for the session (sockets are blocked).
    if (this.transport === "websocket" && this.wsFailures >= WS_FAILURES_BEFORE_SSE) {
      this.transport = "sse";
    }

    const policy: BackoffPolicy = this.transport === "sse" ? SSE_BACKOFF : WEBSOCKET_BACKOFF;
    const delay = backoffDelay(policy, this.attempt, this.random);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private teardownConnection(): void {
    if (this.ws !== null) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    }
    if (this.es !== null) {
      const es = this.es;
      this.es = null;
      es.onopen = es.onmessage = es.onerror = null;
      try {
        es.close();
      } catch {
        /* already closing */
      }
    }
  }
}
